import React, { useId, useState } from "react"
import {
  Avatar, Box, Button, Card, Chip, IconButton, ListItemIcon, ListItemText,
  Menu, MenuItem, Stack, Typography,
} from "@mui/material"
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep"
import EditNoteIcon from "@mui/icons-material/EditNote"
import MoreVertIcon from "@mui/icons-material/MoreVert"
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined"
import CellTowerIcon from "@mui/icons-material/CellTower"
import QrCodeIcon from "@mui/icons-material/QrCode"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded"
import PointOfSaleOutlinedIcon from "@mui/icons-material/PointOfSaleOutlined"
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined"
import WifiRoundedIcon from "@mui/icons-material/WifiRounded"
import WifiOffRoundedIcon from "@mui/icons-material/WifiOffRounded"
import Helpers from "../../../../base/utils/Helpers"
import DynamicIcon from "../../../../base/utils/DynamicIcon"

const statusColors = { COMPLETED: "success", IN_PROGRESS: "info", RETRY: "warning", STOP: "error", START: "info" }
const readable = (value) => value ? String(value).replace(/_/g, " ").toLowerCase().replace(/^./, (letter) => letter.toUpperCase()) : "Not reported"

const ApproveCardItem = (props) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const cardId = useId()
  const name = props.groupName ? Helpers.underscoreRoleFormat(props.groupName) : "Unnamed group"
  const handleClose = () => setAnchorEl(null)
  const totalTerminals = props.groupSummaryDtl?.totTerminals
  const connectedTerminals = props.groupSummaryDtl?.connectedTerminals
  const disconnectedTerminals = totalTerminals != null && connectedTerminals != null
    ? totalTerminals - connectedTerminals : null

  return (
    <Card component="article" aria-labelledby={`${cardId}-name`} variant="outlined" sx={{
      height: "100%", minWidth: 0, display: "flex", flexDirection: "column",
      borderRadius: 3, borderColor: "#dbe5e5", bgcolor: "white",
      boxShadow: "0 3px 12px rgba(21, 65, 67, 0.06)",
      transition: "border-color 150ms, box-shadow 150ms",
      "&:hover": { borderColor: "#9bbfbe", boxShadow: "0 5px 18px rgba(21, 65, 67, 0.10)" },
    }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ p: 2.5, bgcolor: "#116c6f", color: "white" }}>
        <Avatar variant="rounded" sx={{ width: 48, height: 48, bgcolor: "#e1f0ef", color: "#116c6f", borderRadius: 2 }}>
          {props.icon ? <DynamicIcon iconName={props.icon} /> : <GroupsOutlinedIcon />}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography id={`${cardId}-name`} component="h3" sx={{ fontSize: 18, fontWeight: 600, lineHeight: 1.4, overflowWrap: "anywhere" }}>{name}</Typography>
          {props.orgName && <Typography variant="body2" sx={{ mt: 0.5, color: "#e0f0ef", overflowWrap: "anywhere" }}>{Helpers.underscoreRoleFormat(props.orgName)}</Typography>}
          <Typography variant="caption" sx={{ display: "block", mt: 0.75, color: "#d7eeee", overflowWrap: "anywhere" }}>
            Organization code <Box component="span" sx={{ ml: 0.75, color: "white", fontWeight: 600, letterSpacing: 0.5 }}>{props.orgCode || "Not available"}</Box>
          </Typography>
        </Box>
        <IconButton id={`${cardId}-menu-button`} size="small" aria-label={`Actions for ${name}`} aria-haspopup="menu"
          aria-controls={anchorEl ? `${cardId}-menu` : undefined} aria-expanded={anchorEl ? "true" : undefined}
          onClick={(event) => setAnchorEl(event.currentTarget)} sx={{ color: "white" }}><MoreVertIcon /></IconButton>
        <Menu id={`${cardId}-menu`} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose} MenuListProps={{ "aria-labelledby": `${cardId}-menu-button` }}>
          <MenuItem onClick={() => { props.setEditModalOpen(true); handleClose() }}>
            <ListItemIcon><EditNoteIcon /></ListItemIcon><ListItemText>Edit group</ListItemText>
          </MenuItem>
          <MenuItem onClick={() => { props.setDeleteModalopen(true); handleClose() }} sx={{ color: "error.main" }}>
            <ListItemIcon><DeleteSweepIcon color="error" /></ListItemIcon><ListItemText>Delete group</ListItemText>
          </MenuItem>
        </Menu>
      </Stack>

      <Stack spacing={2.25} sx={{ p: 2.5, flex: 1 }}>
        <Box component="dl" sx={{ m: 0, p: 2, borderRadius: 2, bgcolor: "#f0f7f6", border: "1px solid #e0edeb" }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Stack component="dt" direction="row" spacing={1} alignItems="center" sx={{ color: "#466b6c" }}>
            <PointOfSaleOutlinedIcon sx={{ fontSize: 22 }} />
            <Typography component="span" variant="body2">Total terminals</Typography>
          </Stack>
          <Typography component="dd" sx={{ m: 0, fontSize: totalTerminals == null ? 14 : 28, lineHeight: 1.2, fontWeight: 700, color: "#164f51", fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere" }}>{totalTerminals ?? "Not reported"}</Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5, mt: 1.5, pt: 1.5, borderTop: "1px solid #dce8e6" }}>
            {[
              ["Connected", connectedTerminals, WifiRoundedIcon, "#237a4a"],
              ["Disconnected", disconnectedTerminals, WifiOffRoundedIcon, "#b34843"],
            ].map(([label, count, Icon, color]) => <Box key={label} sx={{ minWidth: 0 }}>
              <Stack component="dt" direction="row" spacing={0.75} alignItems="center" sx={{ color }}>
                <Icon sx={{ fontSize: 18 }} />
                <Typography component="span" variant="caption" sx={{ fontSize: 13 }}>{label}</Typography>
              </Stack>
              <Typography component="dd" sx={{ m: 0, mt: 0.5, fontSize: count == null ? 13 : 24, fontWeight: 700, color, fontVariantNumeric: "tabular-nums", overflowWrap: "anywhere" }}>{count ?? "Not reported"}</Typography>
            </Box>)}
          </Box>
        </Box>
        <Box component="section" aria-label="Deployment settings">
          <Typography component="h4" variant="body2" sx={{ fontWeight: 600, color: "#234d50", mb: 1.25 }}>Deployment settings</Typography>
          <Box component="dl" sx={{ m: 0, display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1.5 }}>
            {[["Settlement", props.setting], ["Update mode", props.update]].map(([label, value]) => (
              <Box key={label} sx={{ minWidth: 0 }}>
                <Typography component="dt" variant="caption" sx={{ color: "#61747c" }}>{label}</Typography>
                <Typography component="dd" variant="body2" sx={{ m: 0, mt: 0.5, fontWeight: 600, color: "#354b54", overflowWrap: "anywhere" }}>{value || "Not reported"}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap flexWrap="wrap" gap={1} sx={{ pt: 1.75, borderTop: "1px solid #eaf0f0" }}>
          <Typography variant="body2" sx={{ color: "#61747c" }}>Broadcast status</Typography>
          <Chip size="small" variant="outlined" label={readable(props.deployStatus)} color={statusColors[props.deployStatus] || "default"}
            sx={{ maxWidth: "100%", height: "auto", minHeight: 24, "& .MuiChip-label": { whiteSpace: "normal", py: 0.25 } }} />
        </Stack>
      </Stack>

      <Box sx={{ px: 2.5, py: 1.75, borderTop: "1px solid #e4ecec", bgcolor: "#fafcfc", "& .MuiButton-root": { textTransform: "none", borderRadius: 2, fontSize: 13 } }}>
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={1}>
          <Button variant="contained" disableElevation endIcon={<ArrowForwardRoundedIcon />} onClick={() => props.handleNavigate(props.orgCode, props.groupId)} sx={{ bgcolor: "#116c6f", "&:hover": { bgcolor: "#0c5659" } }}>View terminals</Button>
          <Button startIcon={<CloudUploadOutlinedIcon />} onClick={() => props.setUploadModalOpen(true)} sx={{ color: "#116c6f" }}>Upload APK</Button>
        </Stack>
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={0.75} sx={{ mt: 1, "& .MuiButton-root": { color: "#526b74" } }}>
          <Button size="small" startIcon={<QrCodeIcon />} onClick={() => props.setQrCodeModalOpen(true)}>QR code</Button>
          <Button size="small" startIcon={<CellTowerIcon />} onClick={() => props.setBroadcastModalOpen(true)}>Broadcast</Button>
          <Button size="small" startIcon={<InfoOutlinedIcon />} onClick={() => props.setMoreInfoModalOpen(true)} sx={{ ml: "auto" }}>Details</Button>
        </Stack>
      </Box>
    </Card>
  )
}

export default ApproveCardItem
